/**
 * EVENTSFLO-CREW-PLANNER-READINESS-VISUAL-V1
 *
 * Read-only visual chip rendered next to draggable crew items in planner
 * surfaces. Surfaces the crew member's readiness/onboarding/profile state
 * so admins can see "why" a chip is amber before they drag it onto a slot.
 *
 * Hard rules respected:
 *  - No backend, no data, no filter, no drag/drop, no override changes.
 *  - The chip is purely informational; it never disables the parent.
 *  - Tooltip is the native ``title`` attribute, no extra portals/listeners.
 *
 * Source signals (in priority order):
 *  1. ``readiness_status``       → ready / missing_documents / verification_required
 *                                  / document_expiring / document_expired / inactive
 *  2. ``onboarding_status``      → verified  → "Verified"
 *  3. ``profile_completion_status`` === "incomplete" → "Incomplete"
 *  4. ``status`` === "pending"   → "Pending"
 *  5. Otherwise: render nothing (legacy records with no signal).
 */
import React from "react";

const PRESETS = {
  ready: {
    label: "Ready",
    className:
      "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
    reason: "All requirements met — eligible for assignment.",
  },
  verified: {
    label: "Verified",
    className:
      "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
    reason: "Documents verified.",
  },
  missing_documents: {
    label: "Missing Docs",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40",
    reason: "Required documents are missing.",
  },
  verification_required: {
    label: "Verify",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40",
    reason: "Documents uploaded but pending verification.",
  },
  document_expiring: {
    label: "Expiring",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40",
    reason: "One or more documents expire within 30 days.",
  },
  document_expired: {
    label: "Expired",
    className:
      "bg-red-500/15 text-red-400 border-red-500/40",
    reason: "One or more required documents have expired.",
  },
  inactive: {
    label: "Inactive",
    className:
      "bg-red-500/15 text-red-400 border-red-500/40",
    reason: "Crew is marked inactive.",
  },
  incomplete: {
    label: "Incomplete",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40",
    reason: "Profile is incomplete (missing contact or basic info).",
  },
  pending: {
    label: "Pending",
    className:
      "bg-amber-500/15 text-amber-400 border-amber-500/40",
    reason: "Crew status is pending — not yet activated.",
  },
};

/**
 * Resolve the preset key for a crew member based on its signals.
 * Returns null when no signal is present (legacy data) so the caller
 * can render nothing — keeps the planner UI calm.
 */
function resolvePreset(staff) {
  const r = staff?.readiness_status;
  if (r && PRESETS[r]) return r;
  if (staff?.onboarding_status === "verified") return "verified";
  if (staff?.profile_completion_status === "incomplete") return "incomplete";
  if (staff?.status === "pending") return "pending";
  return null;
}

export function CrewReadinessChip({ staff, scale = 1, className = "" }) {
  const key = resolvePreset(staff);
  if (!key) return null;
  const preset = PRESETS[key];
  // Prefer the backend-provided human reason when available.
  const tooltip = staff?.readiness_reason || preset.reason;
  const fontSize = Math.round(9 * scale);
  const padX = Math.round(5 * scale);
  return (
    <span
      data-testid={`crew-readiness-chip-${staff.id}`}
      data-readiness={key}
      title={tooltip}
      className={[
        "shrink-0 inline-flex items-center rounded-full border leading-none whitespace-nowrap",
        preset.className,
        className,
      ].join(" ")}
      style={{
        fontSize: `${fontSize}px`,
        padding: `${Math.round(2 * scale)}px ${padX}px`,
      }}
    >
      {preset.label}
    </span>
  );
}

export default CrewReadinessChip;
